import dayjs from 'dayjs';

/**
 * 바꾸면 **이미 지나간 달의 정산·매출 숫자가 달라지는** 수강권 필드.
 *
 * 정산이 수강권 문서를 그 달의 스냅샷이 아니라 **현재 값으로 읽기 때문**이다. 관리자가
 * 의도적으로 정정하는 것은 허용된 동작이지만, 모르고 바꾸는 일은 없어야 한다.
 *
 * 목록은 서버가 `shareLifecycle.settlementImpactFields` 로 내려준다. 프론트에서 만들지
 * 않는다 — 서버가 실제로 재동기화하는 필드가 늘거나 줄면 그쪽이 정본이다.
 * (백엔드 `SETTLEMENT_IMPACTING_MEMBERSHIP_FIELDS`)
 */
export const SETTLEMENT_IMPACT_FIELD_LABELS: Record<string, string> = {
    pricePerSchedule: '회당 가격',
    isNewSales: '회원 구분',
    totalPrice: '판매 금액',
    totalSession: '총 회차',
    initialPaymentDate: '결제 일자',
    isSplitPayment: '분할 결제',
    salesManager: '판매 담당자',
};

/**
 * 값 비교 전 정규화.
 *
 * 그대로 비교하면 같은 값도 다르게 읽혀 경고가 남발된다 — 날짜는 같은 날인데 시·분이
 * 다르고, 판매담당자는 매번 새 객체다. 경고가 잦으면 아무도 안 읽는다.
 */
const normalizeFieldValue = (field: string, value: unknown): string => {
    if (value === null || value === undefined) return '';
    if (field === 'initialPaymentDate') {
        // 결제 일자는 날짜만 의미가 있다(달력으로만 고른다). 시각 차이로 경고하지 않는다.
        const parsed = dayjs(value as string);
        return parsed.isValid() ? parsed.format('YYYY-MM-DD') : '';
    }
    if (field === 'salesManager') {
        // 담당자는 userId 로만 같고 다름을 가른다. 이름은 같은 사람이 바뀌어도 같을 수 있다.
        if (typeof value !== 'object') return String(value);
        const userId = (value as { userId?: unknown }).userId;
        return typeof userId === 'string' || typeof userId === 'number' ? String(userId) : '';
    }
    if (typeof value === 'boolean') return value ? 'true' : 'false';
    if (typeof value === 'number') return Number.isFinite(value) ? String(value) : '';
    return String(value).trim();
};

export type SettlementImpactChange = {
    field: string;
    label: string;
};

/**
 * 저장하려는 값 중 정산에 영향을 주는 필드를 관리자가 실제로 **건드렸는지** 가려낸다.
 *
 * `before` 는 수강권 문서가 아니라 **폼의 초기 상태**를 넣는다. 문서와 비교하면 값이
 * 비어 있던 레거시 레코드(`isSplitPayment`·`isNewSales` 가 undefined)에서 폼 기본값
 * false 와 어긋나 사용자가 아무것도 안 건드려도 경고가 뜬다.
 *
 * - `settlementImpactFields` 가 비어 있으면(구버전 서버 · 그룹 수강권 상세) 빈 배열이다.
 *   경고를 못 띄우는 것이지 저장을 막을 이유는 없다 — 예전과 같은 상태다.
 * - 라벨을 모르는 필드는 서버가 목록에 새로 넣은 것이다. 이름을 지어내지 않고 필드명을
 *   그대로 보여준다. 빠뜨리는 것보다 낫다.
 */
export const getSettlementImpactChanges = ({
    settlementImpactFields,
    before,
    after,
}: {
    settlementImpactFields: string[] | null | undefined;
    before: Record<string, unknown> | null | undefined;
    after: Record<string, unknown> | null | undefined;
}): SettlementImpactChange[] => {
    if (!Array.isArray(settlementImpactFields) || settlementImpactFields.length === 0) return [];
    if (!after) return [];

    const seen = new Set<string>();
    const changes: SettlementImpactChange[] = [];
    settlementImpactFields.forEach((field) => {
        if (typeof field !== 'string' || !field.trim() || seen.has(field)) return;
        seen.add(field);
        // 폼이 아예 다루지 않는 필드는 이번 저장으로 바뀔 수 없다.
        if (!(field in after)) return;
        if (
            normalizeFieldValue(field, before?.[field]) ===
            normalizeFieldValue(field, after[field])
        ) {
            return;
        }
        changes.push({ field, label: SETTLEMENT_IMPACT_FIELD_LABELS[field] || field });
    });
    return changes;
};

/**
 * 확인 알럿 본문. 무엇이 왜 바뀌는지 한 문장으로 말한다.
 *
 * "이미 지나간 달"이라고 단정하지 않는다 — 이번 달에 시작한 수강권이면 지난 달 숫자는
 * 움직이지 않는다. 사실보다 센 문구는 신뢰를 깎고, 그러면 다음 경고도 안 읽힌다.
 */
export const buildSettlementImpactConfirmMessage = (
    changes: SettlementImpactChange[],
): string =>
    `${changes
        .map((change) => change.label)
        .join(
            ', ',
        )}을(를) 바꾸면 지난 달을 포함해 이미 집계된 매출·정산 금액도 함께 바뀝니다.\n계속할까요?`;
